import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { useSettings } from "../../contexts/SettingsContext";
import { LockClosedIcon, UserIcon } from "@heroicons/react/24/outline";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login } = useAuth();
  const { settings } = useSettings();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const success = await login(username, password);
    if (!success) {
      setError("Invalid username or password");
    }
    // Login successful, redirect will be handled by the router
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-2xl">
          <div className="text-center mb-8">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${settings.primaryColor}20` }}
            >
              <LockClosedIcon
                className="w-8 h-8"
                style={{ color: settings.primaryColor }}
              />
            </div>
            <h1 className="text-2xl font-oxanium font-bold text-white mb-2">
              Admin Access
            </h1>
            <p className="text-gray-300">
              Enter your credentials to access the admin panel
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  style={
                    {
                      "--focus-ring-color": settings.primaryColor,
                    } as React.CSSProperties
                  }
                  onFocus={(e) =>
                    (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                  }
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                  placeholder="Enter username"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  style={
                    {
                      "--focus-ring-color": settings.primaryColor,
                    } as React.CSSProperties
                  }
                  onFocus={(e) =>
                    (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                  }
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                  placeholder="Enter password"
                  required
                />
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm"
              >
                {error}
              </motion.div>
            )}

            <motion.button
              type="submit"
              className="w-full py-3 px-4 text-white font-semibold rounded-lg transition-colors duration-200"
              style={{ backgroundColor: settings.primaryColor }}
              onMouseEnter={(e) => {
                const color = settings.primaryColor;
                const rgb = color.match(/\d+/g);
                if (rgb) {
                  const r = parseInt(rgb[0]);
                  const g = parseInt(rgb[1]);
                  const b = parseInt(rgb[2]);
                  e.currentTarget.style.backgroundColor = `rgb(${Math.max(
                    0,
                    r - 20
                  )}, ${Math.max(0, g - 20)}, ${Math.max(0, b - 20)})`;
                }
              }}
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = settings.primaryColor)
              }
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Sign In
            </motion.button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
